import type { StatCardModel } from '@/types/stats';
import type { DashboardProjectRow } from '@/data/dashboardProjects';
import { PageHeader } from '@/components/composites/PageHeader';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { DataTable } from '@/components/composites/DataTable';
import { Badge } from '@/components/primitives/Badge';
import { Button } from '@/components/primitives/Button';
import { useDashboardProjects } from '@/hooks/useDashboardData';
import styles from './ProjectDetailPage.module.css';

export function ProjectDetailPage() {
  // Task 145 D3 — useDashboardProjects now returns Client→Project groups for
  // the Dashboard accordion. The legacy ProjectDetailPage flat table just
  // flattens those groups back to a project list; the flat shape was the
  // source of truth pre-145, so the join order is preserved.
  const { data: groups, totals } = useDashboardProjects();
  const rows: DashboardProjectRow[] = groups.flatMap((g) => g.projects);
  const billableHours = rows.filter((r) => r.type === 'billable').reduce((s, r) => s + r.hours, 0);
  const generalHours = rows.filter((r) => r.type === 'general').reduce((s, r) => s + r.hours, 0);

  const cards: readonly [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Total Hours', value: `${totals.hours.toFixed(1)}h` },
    { label: 'Billable Hours', value: `${billableHours.toFixed(1)}h` },
    { label: 'General Hours', value: `${generalHours.toFixed(1)}h` },
    { label: 'Projects', value: rows.length },
  ];

  return (
    <>
      <PageHeader
        title="Projects"
        actions={
          <Button variant="secondary" onClick={() => {}}>
            Export
          </Button>
        }
      />
      <StatCardGrid cards={cards} />
      <DataTable<DashboardProjectRow>
        caption="Projects — Last 30 Days"
        columns={[
          {
            key: 'name',
            header: 'Project',
            render: (r) => <span className={styles.projectName}>{r.name}</span>,
          },
          {
            key: 'client',
            header: 'Client',
            render: (r) => (
              <span className={r.client ? '' : styles.clientMuted}>{r.client ?? '\u2014'}</span>
            ),
          },
          {
            key: 'type',
            header: 'Type',
            render: (r) => (
              <Badge variant={r.type}>{r.type === 'billable' ? 'Billable' : 'General'}</Badge>
            ),
          },
          {
            key: 'hours',
            header: 'Hours',
            align: 'right',
            render: (r) => <span className={styles.mono}>{r.hours.toFixed(1)}h</span>,
          },
          {
            key: 'pct',
            header: '% of Total',
            align: 'right',
            render: (r) => (
              <div className={styles.pctWrap}>
                <span className={styles.mono}>{r.pct.toFixed(1)}%</span>
                <div className={styles.pctBarTrack}>
                  <div className={styles.pctBarFill} style={{ width: `${r.pct}%` }} />
                </div>
              </div>
            ),
          },
          {
            key: 'events',
            header: 'Events',
            align: 'right',
            render: (r) => <span className={styles.mono}>{r.events.toLocaleString()}</span>,
          },
        ]}
        rows={[...rows]}
        getRowKey={(r) => r.name}
        footer={
          <tr>
            <td>Total</td>
            <td className={styles.muted}>{'\u2014'}</td>
            <td className={styles.muted}>{'\u2014'}</td>
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              {totals.hours.toFixed(1)}h
            </td>
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              100%
            </td>
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              {totals.events.toLocaleString()}
            </td>
          </tr>
        }
      />
    </>
  );
}
